/**
 * Zod schemas for DealResponseDto, DealFilters, and panel payloads.
 * BigDecimal fields stay as strings — never coerce to number.
 */
import { z } from 'zod';
import { StpDecisionSchema } from '@/schemas/stp/stpDecision';

const StringishSchema = z.union([z.string(), z.number()]).transform((value) => String(value));
const BigDecimalStringSchema = StringishSchema;
const BuySellSchema = z
  .string()
  .transform((value) => value.toLowerCase());
const NamedReferenceSchema = z.object({
  id: StringishSchema.optional().nullable().catch(null),
  name: z.string().optional().nullable().catch(null),
});
const BookSummarySchema = NamedReferenceSchema.extend({
  entity: z.string().optional().nullable().catch(null),
  legal_entity_id: z.string().optional().nullable().catch(null),
  desk_code: z.string().optional().nullable().catch(null),
});
const ApprovalReferenceSchema = z.object({
  id: z.string().optional().nullable().catch(null),
});

// ---------------------------------------------------------------------------
// Wire shape — matches DealResponseDto (Jackson snake_case)
// ---------------------------------------------------------------------------
export const DealListItemSchema = z.object({
  id: StringishSchema,
  product_type: z.string(),
  asset_class: z.string(),
  status: z.string(),
  notional: BigDecimalStringSchema,
  currency: z.string(),
  effective_date: z.string().optional().nullable(),
  buy_sell: BuySellSchema.optional().nullable(),
});

export type DealListItem = z.infer<typeof DealListItemSchema>;

export const DealResponseSchema = DealListItemSchema.extend({
  product_subtype: z.string().optional().nullable(),
  counterparty_id: StringishSchema.optional().catch(''),
  counterparty: NamedReferenceSchema.optional().nullable().catch(null),
  internal_party_id: StringishSchema.optional().nullable().catch(null),
  book_id: StringishSchema.optional().catch(''),
  book: BookSummarySchema.optional().nullable().catch(null),
  instrument_id: StringishSchema.optional().nullable().catch(null),
  instrument: NamedReferenceSchema.optional().nullable().catch(null),
  approval_id: z.string().optional().nullable().catch(null),
  approval: ApprovalReferenceSchema.optional().nullable().catch(null),
  created_at: z.string().optional().nullable().catch(null),
  updated_at: z.string().optional().nullable().catch(null),
  trade_date: z.string().optional().nullable().catch(null),
  trade_timestamp: z.string().optional().nullable().catch(null),
  booked_at: z.string().optional().nullable().catch(null),
  version: z.number().int().catch(0),
  allowed_transitions: z.array(z.string()).catch([]),
  // Populated only by the findById path; null on list responses and on deals
  // that haven't been STP-evaluated/persisted yet.
  stp_decision: StpDecisionSchema.optional().nullable().catch(null),
  // Populated only by the findById path, mirroring stp_decision above.
  // compliance_pass is deliberately nullable (not optimistic `true`) —
  // there is no ComplianceService yet on the backend, so it is `null` today.
  compliance_pass: z.boolean().optional().nullable().catch(null),
  ssi_exists: z.boolean().optional().nullable().catch(null),
  counterparty_rating: z.string().optional().nullable().catch(null),
  tenor_years: z.number().int().optional().nullable().catch(null),
});

export const DealListResponseSchema = z.array(DealListItemSchema);

export type DealResponse = z.infer<typeof DealResponseSchema>;

// ---------------------------------------------------------------------------
// Query filters — all optional strings (empty string = not applied)
// ---------------------------------------------------------------------------
export const DealFiltersSchema = z.object({
  q: z.string().optional(),
  status: z.string().optional(),
  effectiveDateFrom: z.string().optional(),
  effectiveDateTo: z.string().optional(),
  counterpartyId: z.string().optional(),
  productType: z.string().optional(),
  assetClass: z.string().optional(),
  bookId: z.string().optional(),
  cursor: z.string().optional(),
  pageSize: z.number().int().optional(),
});

export type DealFilters = z.infer<typeof DealFiltersSchema>;

// ---------------------------------------------------------------------------
// Panel payloads
// ---------------------------------------------------------------------------
export const DealListPayloadSchema = z.object({
  /** Filters preserved so chat → detail round-trip stays coherent. */
  filters: DealFiltersSchema.optional(),
});

export type DealListPayload = z.infer<typeof DealListPayloadSchema>;

export const DealDetailPayloadSchema = z.object({
  dealId: z.string().min(1),
  /** Originating list filters for back-navigation context. */
  sourceFilters: DealFiltersSchema.optional(),
});

export type DealDetailPayload = z.infer<typeof DealDetailPayloadSchema>;

export const CouponSchedulePayloadSchema = z.object({
  dealId: z.string().min(1),
});

export type CouponSchedulePayload = z.infer<typeof CouponSchedulePayloadSchema>;

// ---------------------------------------------------------------------------
// Capture request — wire shape sent to POST /api/v1/deals.
// `trade_timestamp` is required since backend PR 376 (DealCaptureRequestDto).
// Backend rejects null, future (>now+1s), and >50yr-old timestamps.
// ---------------------------------------------------------------------------
export const DealCaptureRequestSchema = z.object({
  product_type: z.string().min(1),
  product_subtype: z.string().optional(),
  asset_class: z.string().min(1),
  notional: z.string().min(1),
  currency: z.string().min(3),
  counterparty_id: z.string().min(1),
  book_id: z.string().min(1),
  instrument_id: z.string().optional(),
  effective_date: z.string().min(1),
  trade_timestamp: z.string().datetime({ message: 'trade_timestamp must be an ISO-8601 instant' }),
  buy_sell: z.enum(['BUY', 'SELL']),
  details: z.record(z.string(), z.unknown()).optional(),
  extensions: z.record(z.string(), z.unknown()).optional(),
  instruction_item_id: z.string().optional(),
});

export type DealCaptureRequestInput = z.infer<typeof DealCaptureRequestSchema>;
